import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { get } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import CampaignEntityAvatar from 'components/entity/entity-avatar/CampaignEntityAvatar';
import CampaignEntityName from 'components/entity/entity-name/CampaignEntityName';
import CampaignSummaryGroup from '../campaign-summary/campaign-summary-group/CampaignSummaryGroup';
import CampaignOverviewProgress from '../campaign-overview/campaign-overview-progress/CampaignOverviewProgress';
import CampaignDates from '../campaign-dates/CampaignDates';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import MoreButton from 'components/buttons/more-button/MoreButton';

import { trackEventCampaignHeaderExpanded } from 'modules/tracking/utils/tracking';

import { __t, applyTranslations } from 'utils/language';

import type { Campaign, CampaignOverview } from 'types';

applyTranslations(require('./campaign-info.i18n.json'));

type OwnProps = {
  campaign: Campaign | undefined | null,
  pageType: string,
  contextMenuItems: Array<any>
};

type StateProps = {
  overview: CampaignOverview
};

type Props = OwnProps & StateProps;

type State = {
  isExpanded: boolean
};

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: false
});

class CampaignInfo extends React.PureComponent<Props, State> {
  state = {
    isExpanded: false
  };

  getExpandableClass(baseClass: string): string {
    const { isExpanded } = this.state;

    return classnames(baseClass, {
      [`${baseClass}--expanded`]: isExpanded
    });
  }

  handleExpandable = (): void => {
    const { campaign, pageType } = this.props;

    this.setState(state => ({
      isExpanded: !state.isExpanded
    }), () => {
      const { isExpanded } = this.state;

      if (isExpanded && campaign) {
        trackEventCampaignHeaderExpanded(pageType);
      }
    });
  };

  renderEntityAvatar(campaign: Campaign): React.ReactNode {
    return (
      <div className="campaign-info__image">
        <CampaignEntityAvatar
          disabled
          campaign={campaign}
          form="square"
          size="customSize"
          customSize={{
            width: 218,
            height: 218
          }}
          applyCustomElementSize={false}
          className={this.getExpandableClass('campaign-info__avatar')}
        />
      </div>
    );
  }

  renderEntityName(campaign: Campaign): React.ReactNode {
    return (
      <div className="campaign-info__title text-overflow-ellipsis">
        <CampaignEntityName
          disabled
          campaign={campaign}
          size="inherit"
        />
      </div>
    );
  }

  renderSummaryGroups(): React.ReactNode {
    const { campaign, overview } = this.props;
    const plannedBudget = campaign?.budget ?? 0;
    const usedBudget = overview?.usedBudget ?? 0;
    const variablePayments = overview?.legallyBindingVariablePaymentsSum ?? 0;
    const remainingBudget = plannedBudget - usedBudget;

    return (
      <React.Fragment>
        <CampaignSummaryGroup
          title={__t('campaign_info.campaign_summary_pending')}
          budget={get(overview, 'pendingOverview.budget', 0)}
          followers={get(overview, 'pendingOverview.followers', 0)}
          count={get(overview, 'pendingOverview.creators', 0)}
        />
        <CampaignSummaryGroup
          title={__t('campaign_info.campaign_summary_agreed')}
          budget={get(overview, 'acceptedOverview.budget', 0)}
          followers={get(overview, 'acceptedOverview.followers', 0)}
          count={get(overview, 'acceptedOverview.creators', 0)}
        />
        {
          Boolean(variablePayments) && (
            <CampaignSummaryGroup
              title={__t('campaign_info.campaign_summary_variable_payments')}
              variablePayments={variablePayments}
              showOnlyVariablePayments
            />
          )
        }
        <CampaignSummaryGroup
          title={__t('campaign_info.campaign_summary_used_budget')}
          budget={usedBudget}
          display="flex"
          showOnlyBudget
        />
        <CampaignSummaryGroup
          title={__t('campaign_info.campaign_summary_remaining_budget')}
          budget={remainingBudget}
          display="flex"
          showOnlyBudget
          showNotApplicable={!plannedBudget}
        />

      </React.Fragment>
    );
  }

  renderDefault(): React.ReactNode {
    const { campaign, pageType } = this.props;

    return (
      <React.Fragment>
        {campaign && this.renderEntityAvatar(campaign)}
        <div className="campaign-info__content">
          <div className="campaign-info__content-default">
            {
              campaign && this.renderEntityName(campaign)
            }
            {
              campaign && <CampaignDates campaign={campaign} />
            }
          </div>
          <div className="campaign-info__content-expanded">
            {
              pageType !== 'client-approval' &&
                this.renderExpandable()
            }
            {
              pageType === 'client-approval' &&
                this.renderWithContextMenu()
            }
          </div>
        </div>
      </React.Fragment>
    );
  }

  renderWithContextMenu(): React.ReactNode {
    const { contextMenuItems } = this.props;

    if (contextMenuItems && contextMenuItems.length > 0) {
      return (
        <div className="campaign-info__context-menu">
          <MoreButtonWithMenu contextMenuProps={{ items: contextMenuItems }} width={40} />
        </div>
      );
    }

    return null;
  }

  renderExpandable(): React.ReactNode {
    const { campaign, overview } = this.props;
    const variablePayments = get(overview, 'legallyBindingVariablePaymentsSum', 0);

    const groupsClasses = classnames('campaign-info__expandable-groups', {
      'campaign-info__expandable-groups--with-variable-payments': Boolean(variablePayments)
    });

    return (
      <div className={this.getExpandableClass('campaign-info__expandable')}>
        <div
          className={this.getExpandableClass('campaign-info__expandable-icon')}
          onClick={this.handleExpandable}
        >
          <SVGDynamicIcon
            name="chevron-down-custom-fg"
            width={14}
            height={7}
            customColor="grey"
            clickable
          />
        </div>
        <div className={this.getExpandableClass('campaign-info__expandable-details')}>
          <div className="campaign-info__expandable-progress">
            {
              campaign && (
                <CampaignOverviewProgress
                  campaign={campaign}
                  overview={overview}
                  title={__t('campaign_info.campaign_summary_planned_budget')}
                />
              )
            }
          </div>
          <div className={groupsClasses}>
            {this.renderSummaryGroups()}
          </div>
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    const { campaign } = this.props;

    if (!campaign || !campaign.name || !campaign.startDate || !campaign.endDate) {
      return null;
    }

    return (
      <div className="campaign-info">
        {this.renderDefault()}
      </div>
    );
  }
}

const mapStateToProps = ({ campaignOverview }): StateProps => ({
  overview: campaignOverview.overview
});

export default connect(mapStateToProps)(CampaignInfo);
